/**
 * Collection Workflow Tab for NCR Edit
 *
 * The CUSTOMER initiates the collection with a proposed date, the SUPPLIER
 * confirms the date (tracking, notes), the CUSTOMER confirms the goods were
 * collected, the SUPPLIER confirms receipt. The customer's initiate step was
 * dropped by the spec flow (QUALITY-NCR-REVIEW, 2 Oct 2026) and is restored
 * (19 Oct 2026); the steps after it are the spec's.
 */

import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Package, Truck, CheckCircle2, PackageCheck,
} from 'lucide-react';
import type { NonConformanceReport as NCRType } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { daysWaitingText } from '../../components/quality/ncrPlate';
import type { NCRDocument } from '../../lib/api';
import { DocumentsSection } from './DocumentsSection';
import { todayLocalISO } from './localDate';
import { ProcessStepper, COLLECTION_STEPS, collectionStep } from './ProcessStepper';
import { CollectionAssignmentPanel } from './CollectionAssignment';
import { InitiateCollectionForm, type InitiateCollectionData } from './CustomerProcessRequests';

// T8: the step boxes are shared with Credit and Replacement (ProcessStepper).

export interface CollectionWorkflowTabProps {
  ncr: NCRType;
  isCustomer: boolean;
  isSupplier: boolean;
  saving: boolean;
  /** The customer's first step: initiate with a proposed date. */
  onInitiate?: (data: InitiateCollectionData) => void;
  onUpdateStatus: (status: string, data?: Record<string, unknown>) => void;
  /** B9 (spec collection states 10-13): attachments either side adds here. */
  documents?: NCRDocument[];
  onDocumentsChanged?: () => void;
  /** CR-7352: (re)assign the collection, or null to clear its owner. */
  onAssign?: (assignedTo: number | null) => void;
  /** CR-7352: the assignee may complete early. */
  currentUserId?: number;
  /** CR-7352: a customer admin in Master Edit may also complete early. */
  masterEditActive?: boolean;
}

// The viewer's own calendar day (toISOString gave the UTC day, a day behind after midnight BST).
const today = todayLocalISO;

export function CollectionWorkflowTab({
  ncr, isCustomer, isSupplier, saving, onInitiate, onUpdateStatus, documents, onDocumentsChanged,
  onAssign, currentUserId, masterEditActive = false,
}: CollectionWorkflowTabProps) {
  const { formatDate } = useFormatters();
  const process = ncr.collection_process;
  const [proposal, setProposal] = useState({ confirmed_collection_date: '', tracking_reference: '', collection_notes: '' });
  const [collected, setCollected] = useState({ actual_collection_date: today(), customer_notes: '' });
  const [receipt, setReceipt] = useState({ received_date: today(), supplier_receipt_notes: '' });
  const [early, setEarly] = useState({ actual_collection_date: today(), customer_notes: '' });

  if (!ncr.is_collection_required) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8 text-muted-foreground">
            Collection is not required for this NCR.
          </div>
        </CardContent>
      </Card>
    );
  }

  const status = process?.status;
  const step = collectionStep(status, !!ncr.collection_completed);
  const waiting = daysWaitingText(ncr.days_waiting?.collection);
  // CR-7352 + spec flow (Aaron, 30 Sep 2026): the assignee owns the customer's
  // step; they (or an admin in Master Edit) may also complete early, before
  // the supplier confirms (round 2: once confirmed, the supplier's receipt is
  // its own step, as on development).
  const isAssignee = currentUserId != null && process?.assigned_to === currentUserId;
  const canCompleteEarly = isCustomer && step <= 1 && (isAssignee || masterEditActive);
  // The supplier's date starts at the customer's proposed one.
  const confirmDate = proposal.confirmed_collection_date || process?.proposed_collection_date || '';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-2"><Package className="h-5 w-5" /> Collection</span>
          {waiting && (
            <span className="text-sm font-normal tabular-nums text-muted-foreground">Collection: {waiting}</span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <ProcessStepper steps={COLLECTION_STEPS} current={step} />

        {process && (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {process.proposed_collection_date && (
              <div><dt className="inline text-muted-foreground">Proposed date: </dt>
                <dd className="inline font-medium">{formatDate(process.proposed_collection_date)}</dd></div>
            )}
            {process.collection_address && (
              <div><dt className="inline text-muted-foreground">Collection address: </dt>
                <dd className="inline font-medium">{process.collection_address}</dd></div>
            )}
            {process.confirmed_collection_date && (
              <div><dt className="inline text-muted-foreground">Collection date: </dt>
                <dd className="inline font-medium">{formatDate(process.confirmed_collection_date)}</dd></div>
            )}
            {process.tracking_reference && (
              <div><dt className="inline text-muted-foreground">Tracking: </dt>
                <dd className="inline font-medium">{process.tracking_reference}</dd></div>
            )}
            {process.actual_collection_date && (
              <div><dt className="inline text-muted-foreground">Collected on: </dt>
                <dd className="inline font-medium">{formatDate(process.actual_collection_date)}</dd></div>
            )}
            {process.received_date && (
              <div><dt className="inline text-muted-foreground">Received by supplier: </dt>
                <dd className="inline font-medium">{formatDate(process.received_date)}</dd></div>
            )}
            {process.collection_notes && (
              <div className="col-span-2"><dt className="inline text-muted-foreground">Supplier collection notes: </dt>
                <dd className="inline">{process.collection_notes}</dd></div>
            )}
            {process.customer_notes && (
              <div className="col-span-2"><dt className="inline text-muted-foreground">Customer notes: </dt>
                <dd className="inline">{process.customer_notes}</dd></div>
            )}
            {process.supplier_receipt_notes && (
              <div className="col-span-2"><dt className="inline text-muted-foreground">Supplier receipt notes: </dt>
                <dd className="inline">{process.supplier_receipt_notes}</dd></div>
            )}
          </dl>
        )}

        {/* CR-7352: who on the customer's side does "Goods collected". Not the
            supplier's business, so hidden from them. Before the collection
            exists, the Initiate form carries the owner instead. */}
        {isCustomer && onAssign && process && (
          <CollectionAssignmentPanel
            ncrId={ncr.id}
            assignedTo={process?.assigned_to ?? null}
            assignedToName={process?.assigned_to_name}
            editable={step < COLLECTION_STEPS.length}
            saving={saving}
            onAssign={onAssign}
          />
        )}

        {canCompleteEarly && (
          <div className="border-t pt-4 space-y-3">
            <h4 className="font-medium text-sm">Complete early</h4>
            <p className="text-xs text-muted-foreground">
              {step === 0 ? "The collection hasn't been initiated yet." : "The supplier hasn't confirmed the collection yet."}
              {' '}You can still mark the collection complete; the log will say it was before supplier confirmation.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="early_collection_date">Actual Collection Date <span className="text-red-500">*</span></Label>
                <Input id="early_collection_date" type="date" max={today()} value={early.actual_collection_date}
                  onChange={e => setEarly(f => ({ ...f, actual_collection_date: e.target.value }))} />
              </div>
            </div>
            <Textarea value={early.customer_notes} rows={2} placeholder="Notes..."
              onChange={e => setEarly(f => ({ ...f, customer_notes: e.target.value }))} />
            <Button variant="outline" disabled={saving || !early.actual_collection_date}
              onClick={() => onUpdateStatus('completed', {
                ...early,
                ...(masterEditActive && !isAssignee ? { master_edit: true } : {}),
              })}>
              <CheckCircle2 className="h-4 w-4 mr-2" /> Complete early
            </Button>
          </div>
        )}

        {/* Step 1 — the customer initiates the collection with a proposed date. */}
        {step === 0 && (isCustomer ? (
          onInitiate && (
            <InitiateCollectionForm ncrId={ncr.id} saving={saving} showAssignee={!process} initial={process ?? undefined} onInitiate={onInitiate} />
          )
        ) : (
          <p className="text-sm text-sky-700">Waiting for the customer to initiate the collection process.</p>
        ))}

        {/* Step 2 — the supplier confirms the collection date. */}
        {step === 1 && (isSupplier ? (
          <div className="border-t pt-4 space-y-4">
            <h4 className="font-medium text-sm">Confirm collection date</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Collection Date <span className="text-red-500">*</span></Label>
                <Input type="date" required value={confirmDate}
                  onChange={e => setProposal(p => ({ ...p, confirmed_collection_date: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Tracking Reference</Label>
                <Input value={proposal.tracking_reference} placeholder="Carrier or shipment reference"
                  onChange={e => setProposal(p => ({ ...p, tracking_reference: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Supplier Collection Notes</Label>
              <Textarea rows={2} value={proposal.collection_notes}
                onChange={e => setProposal(p => ({ ...p, collection_notes: e.target.value }))} />
            </div>
            <Button onClick={() => onUpdateStatus('confirmed', { ...proposal, confirmed_collection_date: confirmDate })}
              disabled={saving || !confirmDate}>
              <CheckCircle2 className="h-4 w-4 mr-2" /> Confirm collection date
            </Button>
          </div>
        ) : (
          <p className="text-sm text-amber-700">Waiting for the supplier to confirm the collection date.</p>
        ))}

        {/* Step 3 — the customer confirms the goods were collected. */}
        {step === 2 && (isCustomer ? (
          <div className="border-t pt-4 space-y-4">
            <h4 className="font-medium text-sm">Collection confirmation</h4>
            <div className="space-y-2 max-w-xs">
              <Label>Actual Collection Date <span className="text-red-500">*</span></Label>
              <Input type="date" required max={today()} value={collected.actual_collection_date}
                onChange={e => setCollected(c => ({ ...c, actual_collection_date: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Customer Notes</Label>
              <Textarea rows={2} value={collected.customer_notes}
                onChange={e => setCollected(c => ({ ...c, customer_notes: e.target.value }))} />
            </div>
            <Button onClick={() => onUpdateStatus('collected', collected)}
              disabled={saving || !collected.actual_collection_date}>
              <Truck className="h-4 w-4 mr-2" /> Confirm collection
            </Button>
          </div>
        ) : (
          <p className="text-sm text-sky-700">Waiting for the customer to confirm the goods were collected.</p>
        ))}

        {/* Step 4 — the supplier confirms receipt. */}
        {step === 3 && (isSupplier ? (
          <div className="border-t pt-4 space-y-4">
            <h4 className="font-medium text-sm">Receipt confirmation</h4>
            <div className="space-y-2 max-w-xs">
              <Label>Received Date <span className="text-red-500">*</span></Label>
              <Input type="date" required max={today()} value={receipt.received_date}
                onChange={e => setReceipt(r => ({ ...r, received_date: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Supplier Receipt Notes</Label>
              <Textarea rows={2} value={receipt.supplier_receipt_notes}
                onChange={e => setReceipt(r => ({ ...r, supplier_receipt_notes: e.target.value }))} />
            </div>
            <Button onClick={() => onUpdateStatus('completed', receipt)}
              disabled={saving || !receipt.received_date}>
              <PackageCheck className="h-4 w-4 mr-2" /> Confirm receipt
            </Button>
          </div>
        ) : (
          <p className="text-sm text-amber-700">Waiting for the supplier to confirm they received the goods.</p>
        ))}

        {documents && onDocumentsChanged && (
          <DocumentsSection
            ncrId={ncr.id}
            documents={documents}
            onDocumentsChanged={onDocumentsChanged}
            section="collection"
            title="Collection attachments"
            embedded
          />
        )}

        {step === COLLECTION_STEPS.length && (
          <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <div>
              <h3 className="font-semibold text-green-800">Collection completed</h3>
              {process?.completed_date && (
                <p className="text-sm text-green-600">
                  Receipt confirmed on {formatDate(process.completed_date)}
                  {process.completed_by_name && ` by ${process.completed_by_name}`}
                </p>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
